import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Container, PageTitle, NbCard, NbButton, Chip } from "@/components/nb";
import { Stagger, Item } from "@/components/motion";
import { Heart, Search, Sparkles, Play, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function IdeasExplore() {
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [query, setQuery] = useState("");
  const [explore, setExplore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openVideo, setOpenVideo] = useState(false);

  useEffect(() => { api.get("/ideas/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => {
    const url = active ? `/ideas?category=${active}` : "/ideas";
    api.get(url).then((r) => setIdeas(r.data));
  }, [active]);

  const filteredIdeas = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ideas;
    return ideas.filter((it) => (it.title || "").toLowerCase().includes(q) || (it.description || "").toLowerCase().includes(q));
  }, [ideas, query]);

  const runExplore = async () => {
    const q = query.trim();
    if (!q) return toast.error("Type an interest first");
    setLoading(true);
    setExplore(null);
    try {
      const r = await api.post("/ideas/explore", { query: q });
      setExplore(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI is napping. Try again.");
    } finally { setLoading(false); }
  };

  const like = async (id) => {
    const r = await api.post(`/ideas/${id}/like`);
    setIdeas((list) => list.map((x) => (x.id === id ? r.data : x)));
  };

  return (
    <Container>
      <PageTitle eyebrow="EXPLORE IDEAS" title="What are you curious about?" sub="Type any interest to get an AI guide, roadmap, video and mentor. Or browse student ideas below." />

      {/* SEARCH */}
      <div className="mb-6">
        <div className="flex items-center gap-3 bg-white nb-border nb-shadow rounded-xl px-4 py-3 max-w-3xl">
          <Search className="w-5 h-5 shrink-0" />
          <input
            data-testid="explore-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runExplore(); }}
            placeholder='Try "climate tech", "mental health app", "sports coaching"...'
            className="flex-1 bg-transparent outline-none py-1"
          />
          {query && <button onClick={() => { setQuery(""); setExplore(null); }} data-testid="clear-explore" className="font-mono-sl text-[10px] uppercase underline">Clear</button>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <NbButton onClick={runExplore} color="black" disabled={loading || !query.trim()} data-testid="run-explore">
            <Sparkles className="w-4 h-4 inline-block mr-1" /> {loading ? "Exploring..." : "Explore with AI"}
          </NbButton>
          <span className="font-mono-sl text-[10px] uppercase text-neutral-600 ml-1">or filter categories ↓</span>
        </div>
      </div>

      {/* EXPLORATION RESULT */}
      {explore && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-10" data-testid="explore-result">
          <NbCard color="primary" className="!p-6">
            <Chip color="tertiary">EXPLORING · {explore.query.toUpperCase()}</Chip>
            <h2 className="mt-3 font-heading font-black text-3xl md:text-4xl tracking-tighter">{explore.title}</h2>
            <p className="mt-3 text-neutral-800">{explore.guide}</p>
          </NbCard>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-5 mt-5">
            {/* Video */}
            <div className="md:col-span-3">
              {explore.video?.youtube_id ? (
                <button onClick={() => setOpenVideo(true)} data-testid="explore-video" className="w-full text-left">
                  <NbCard color="accent" className="!p-5 flex items-center gap-4 nb-hover">
                    <div className="w-14 h-14 bg-white nb-border rounded-lg grid place-items-center shrink-0"><Play className="w-6 h-6" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-sl text-[10px] uppercase">TEACHER VIDEO</div>
                      <div className="font-heading font-black text-xl tracking-tight truncate">{explore.video.title}</div>
                    </div>
                    <Chip color="tertiary">WATCH</Chip>
                  </NbCard>
                </button>
              ) : (
                <a href={explore.video?.search_url} target="_blank" rel="noreferrer" data-testid="explore-video-search">
                  <NbCard color="accent" className="!p-5 flex items-center gap-4 nb-hover">
                    <div className="w-14 h-14 bg-white nb-border rounded-lg grid place-items-center shrink-0"><Play className="w-6 h-6" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-sl text-[10px] uppercase">SEARCH YOUTUBE</div>
                      <div className="font-heading font-black text-xl tracking-tight truncate">{explore.video?.title}</div>
                    </div>
                    <Chip color="tertiary">OPEN</Chip>
                  </NbCard>
                </a>
              )}

              <h3 className="font-heading font-black text-xl tracking-tight mt-6 mb-3">Roadmap</h3>
              <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {explore.roadmap.map((s, i) => (
                  <Item key={i}>
                    <NbCard color={["primary", "secondary", "tertiary", "accent"][i % 4]} className="!p-5 h-full">
                      <Chip color="accent">STAGE {i + 1}</Chip>
                      <h4 className="mt-2 font-heading font-black text-lg tracking-tight">{s.stage}</h4>
                      <ul className="mt-3 space-y-2">
                        {s.items.map((it, j) => (
                          <li key={j} className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 mt-1 shrink-0" /><span>{it}</span></li>
                        ))}
                      </ul>
                    </NbCard>
                  </Item>
                ))}
              </Stagger>
            </div>

            {/* Mentor */}
            <div className="md:col-span-2">
              <NbCard className="!p-5" data-testid="explore-mentor">
                <div className="font-mono-sl text-[10px] uppercase">RECOMMENDED MENTOR</div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-sl-primary nb-border grid place-items-center font-heading font-black text-xl">{explore.mentor.avatar}</div>
                  <div>
                    <div className="font-heading font-black text-lg">{explore.mentor.name}</div>
                    <div className="text-sm text-neutral-700">{explore.mentor.role}</div>
                  </div>
                </div>
                <Chip color="tertiary">{explore.mentor.focus.toUpperCase()}</Chip>
                <p className="mt-3 text-sm text-neutral-800">{explore.mentor.bio}</p>
                <div className="mt-3"><a href="/career/mentors"><NbButton color="primary">Meet mentors →</NbButton></a></div>
              </NbCard>
            </div>
          </div>

          {openVideo && explore.video?.youtube_id && (
            <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-5" onClick={() => setOpenVideo(false)} data-testid="explore-video-modal">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-4xl bg-white nb-border nb-shadow-xl rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b-2 border-black flex justify-between items-center">
                  <div className="font-heading font-bold truncate">{explore.video.title}</div>
                  <button onClick={() => setOpenVideo(false)} data-testid="close-explore-video" className="p-2 nb-border rounded-lg bg-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="aspect-video bg-black">
                  <iframe title={explore.video.title} className="w-full h-full" src={`https://www.youtube.com/embed/${explore.video.youtube_id}?rel=0&autoplay=1`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      )}

      {/* COMMUNITY IDEAS */}
      <h3 className="font-heading font-black text-2xl tracking-tight mb-3">Student ideas · {filteredIdeas.length}</h3>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="cat-filter">
        <button onClick={() => setActive(null)} data-testid="cat-all" className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${!active ? "bg-sl-primary nb-shadow" : "bg-white"}`}>All</button>
        {cats.map((c) => (
          <button key={c.id} onClick={() => setActive(c.id)} data-testid={`cat-btn-${c.id}`} className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${active === c.id ? "bg-sl-primary nb-shadow" : "bg-white"}`}>{c.title}</button>
        ))}
      </div>

      {filteredIdeas.length === 0 && (
        <NbCard color="tertiary" data-testid="no-community">
          <div className="font-heading font-bold">No community ideas here yet — try the AI Explore above 👆</div>
        </NbCard>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="idea-list">
        {filteredIdeas.map((it) => (
          <NbCard key={it.id} className="!p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Chip color="tertiary">{it.category?.toUpperCase()}</Chip>
              <button onClick={() => like(it.id)} data-testid={`like-${it.id}`} className="flex items-center gap-1 text-sm nb-border px-2 py-1 rounded-full bg-white nb-hover">
                <Heart className="w-3 h-3" /> {it.likes || 0}
              </button>
            </div>
            <h3 className="font-heading font-black text-xl tracking-tight">{it.title}</h3>
            <p className="text-neutral-800 text-sm">{it.description}</p>
            <div className="font-mono-sl text-[10px] uppercase text-neutral-500">by {it.author}</div>
          </NbCard>
        ))}
      </div>
    </Container>
  );
}
